import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [currentZone, setCurrentZone] = useState(null);
  const { token } = useAuth();

  // Initialize socket connection
  useEffect(() => {
    const serverURL = process.env.REACT_APP_API_URL || 'http://localhost:5005';

    const socketInstance = io(serverURL, {
      auth: {
        token: token || null
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5
    });

    socketInstance.on('connect', () => {
      console.log('✅ Socket.io connected:', socketInstance.id);
      setIsConnected(true);
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('❌ Socket.io disconnected:', reason);
      setIsConnected(false);
    });

    socketInstance.on('connect_error', (error) => {
      console.error('Socket.io connection error:', error);
      setIsConnected(false);
    });

    setSocket(socketInstance);

    // Cleanup on unmount
    return () => {
      socketInstance.disconnect();
    };
  }, [token]);

  // Listen for safety alerts
  useEffect(() => {
    if (!socket) return;

    const handleSafetyAlert = (data) => {
      console.log('🚨 Safety Alert:', data);

      const alert = {
        id: Date.now() + Math.random(),
        ...data,
        timestamp: new Date(data.timestamp)
      };

      setAlerts(prev => [alert, ...prev].slice(0, 50)); // Keep last 50 alerts
    };

    const handleHarassmentAlert = (data) => {
      console.log('⚠️  Harassment Alert:', data);

      const alert = {
        id: Date.now() + Math.random(),
        type: 'harassment-alert',
        ...data,
        timestamp: new Date(data.timestamp),
        priority: 'high'
      };

      setAlerts(prev => [alert, ...prev].slice(0, 50));
    };

    const handleEmergencyAlert = (data) => {
      console.log('🚨 EMERGENCY SOS:', data);

      const alert = {
        id: Date.now() + Math.random(),
        type: 'emergency-alert',
        ...data,
        timestamp: new Date(data.timestamp),
        priority: 'critical'
      };

      setAlerts(prev => [alert, ...prev].slice(0, 50));
    };

    const handleZoneStatusUpdate = (data) => {
      console.log('🗺️  Zone Status Update:', data);

      const alert = {
        id: Date.now() + Math.random(),
        type: 'zone-update',
        ...data,
        timestamp: new Date(data.timestamp)
      };

      setAlerts(prev => [alert, ...prev].slice(0, 50));
    };

    const handleReportVerified = (data) => {
      console.log('✅ Report Verified:', data);

      // Update existing alert if it exists
      setAlerts(prev => prev.map(alert =>
        alert.reportId === data.reportId
          ? { ...alert, verificationStatus: data.verificationStatus }
          : alert
      ));
    };

    const handleIncidentUpdate = (data) => {
      console.log('🔄 Incident Update:', data);

      // Update existing alert
      setAlerts(prev => prev.map(alert =>
        alert.incidentId === data.incidentId
          ? { ...alert, status: data.status, updateType: data.updateType }
          : alert
      ));
    };

    socket.on('safety-alert', handleSafetyAlert);
    socket.on('harassment-alert', handleHarassmentAlert);
    socket.on('emergency-alert', handleEmergencyAlert);
    socket.on('zone-status-update', handleZoneStatusUpdate);
    socket.on('report-verified', handleReportVerified);
    socket.on('incident-status-update', handleIncidentUpdate);

    return () => {
      socket.off('safety-alert', handleSafetyAlert);
      socket.off('harassment-alert', handleHarassmentAlert);
      socket.off('emergency-alert', handleEmergencyAlert);
      socket.off('zone-status-update', handleZoneStatusUpdate);
      socket.off('report-verified', handleReportVerified);
      socket.off('incident-status-update', handleIncidentUpdate);
    };
  }, [socket]);

  /**
   * Join a geofenced safety zone based on user's location
   */
  const joinSafetyZone = useCallback((latitude, longitude, radius = 5000) => {
    if (!socket) {
      console.warn('Socket not connected');
      return;
    }

    socket.emit('join-safety-zone', { latitude, longitude, radius });

    socket.once('zone-joined', (data) => {
      console.log('✅ Joined safety zone:', data);
      setCurrentZone(data);
    });
  }, [socket]);

  /**
   * Leave current safety zone
   */
  const leaveSafetyZone = useCallback(() => {
    if (!socket) return;

    socket.emit('leave-safety-zone');
    setCurrentZone(null);
  }, [socket]);

  /**
   * Update user location (when moving)
   */
  const updateLocation = useCallback((latitude, longitude, radius = 5000) => {
    if (!socket) return;

    socket.emit('update-location', { latitude, longitude, radius });
  }, [socket]);

  /**
   * Send emergency SOS
   */
  const sendEmergencySOS = useCallback((latitude, longitude, message = 'Emergency assistance needed') => {
    if (!socket) {
      console.error('Socket not connected');
      return;
    }

    socket.emit('emergency-sos', { latitude, longitude, message });
    console.log('🚨 Emergency SOS sent');
  }, [socket]);

  /**
   * Clear a specific alert
   */
  const dismissAlert = useCallback((alertId) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
  }, []);

  /**
   * Clear all alerts
   */
  const clearAllAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  /**
   * Get unread alerts count
   */
  const getUnreadCount = useCallback(() => {
    return alerts.filter(alert => !alert.read).length;
  }, [alerts]);

  /**
   * Mark alert as read
   */
  const markAlertAsRead = useCallback((alertId) => {
    setAlerts(prev => prev.map(alert =>
      alert.id === alertId ? { ...alert, read: true } : alert
    ));
  }, []);

  const value = {
    socket,
    isConnected,
    alerts,
    currentZone,
    joinSafetyZone,
    leaveSafetyZone,
    updateLocation,
    sendEmergencySOS,
    dismissAlert,
    clearAllAlerts,
    getUnreadCount,
    markAlertAsRead
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
